import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { useTheme } from './ThemeContext';

function Settings({ user, onSignOut, onBack }) {
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState('profile');
  const [displayName, setDisplayName] = useState(user.user_metadata?.full_name || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [saving, setSaving] = useState(false);

  // Preferences state
  const [continuousListening, setContinuousListening] = useState(true);
  const [autoPlayResponses, setAutoPlayResponses] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const savedPrefs = localStorage.getItem('syntari_preferences');
    if (savedPrefs) {
      try {
        const prefs = JSON.parse(savedPrefs);
        setContinuousListening(prefs.continuousListening ?? true);
        setAutoPlayResponses(prefs.autoPlayResponses ?? true);
        setPushNotifications(prefs.pushNotifications ?? false);
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    }
  }, []);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    const prefs = {
      continuousListening,
      autoPlayResponses,
      pushNotifications,
    };
    localStorage.setItem('syntari_preferences', JSON.stringify(prefs));
  }, [continuousListening, autoPlayResponses, pushNotifications]);

  const handleSaveName = async () => {
    if (!displayName.trim()) {
      alert('Name cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({
        data: { full_name: displayName.trim() }
      });

      if (error) throw error;

      // Also update in users table
      await supabase
        .from('users')
        .update({ display_name: displayName.trim() })
        .eq('id', user.id);

      setIsEditingName(false);
      alert('Name updated successfully!');
    } catch (error) {
      console.error('Error updating name:', error);
      alert('Failed to update name: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetOnboarding = () => {
    localStorage.removeItem('syntari_onboarding_seen');
    alert('Onboarding reset! You will see the welcome message next time you visit the Voice Chat page.');
  };

  const handleClearPreferences = () => {
    if (window.confirm('Are you sure you want to reset all preferences to default?')) {
      setContinuousListening(true);
      setAutoPlayResponses(true);
      setPushNotifications(false);
      alert('Preferences reset to default!');
    }
  };

  const styles = getStyles(theme);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button onClick={onBack} style={styles.backButton}>
          ← Back
        </button>
        <h1 style={styles.title}>Settings</h1>
      </div>

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('profile')}
          style={{
            ...styles.tab,
            borderBottom: activeTab === 'profile' ? `2px solid ${theme.primary}` : '2px solid transparent',
            color: activeTab === 'profile' ? theme.primary : theme.textMuted,
          }}
        >
          Profile
        </button>
        <button
          onClick={() => setActiveTab('preferences')}
          style={{
            ...styles.tab,
            borderBottom: activeTab === 'preferences' ? `2px solid ${theme.primary}` : '2px solid transparent',
            color: activeTab === 'preferences' ? theme.primary : theme.textMuted,
          }}
        >
          Preferences
        </button>
        <button
          onClick={() => setActiveTab('about')}
          style={{
            ...styles.tab,
            borderBottom: activeTab === 'about' ? `2px solid ${theme.primary}` : '2px solid transparent',
            color: activeTab === 'about' ? theme.primary : theme.textMuted,
          }}
        >
          About
        </button>
      </div>

      <div style={styles.content}>
        {activeTab === 'profile' && (
          <div style={styles.section}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Profile Information</h3>

              <div style={styles.infoRow}>
                <span style={styles.label}>Email</span>
                <span style={styles.value}>{user.email}</span>
              </div>

              <div style={styles.infoRow}>
                <span style={styles.label}>Name</span>
                {isEditingName ? (
                  <div style={styles.nameEditContainer}>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      style={styles.nameInput}
                      placeholder="Enter your name"
                    />
                    <button
                      onClick={handleSaveName}
                      style={styles.saveButton}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setDisplayName(user.user_metadata?.full_name || '');
                        setIsEditingName(false);
                      }}
                      style={styles.cancelButton}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div style={styles.nameDisplay}>
                    <span style={styles.value}>
                      {user.user_metadata?.full_name || 'Not set'}
                    </span>
                    <button
                      onClick={() => setIsEditingName(true)}
                      style={styles.editButton}
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>

              <div style={styles.infoRow}>
                <span style={styles.label}>User ID</span>
                <span style={styles.valueSmall}>{user.id}</span>
              </div>
            </div>

            <button onClick={onSignOut} style={styles.signOutButton}>
              Sign Out
            </button>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div style={styles.section}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Voice Settings</h3>

              <div style={styles.settingRow}>
                <div>
                  <div style={styles.settingLabel}>Continuous Listening</div>
                  <div style={styles.settingDesc}>
                    Keep microphone active after responses
                  </div>
                </div>
                <div style={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={continuousListening}
                    onChange={(e) => setContinuousListening(e.target.checked)}
                    style={styles.checkbox}
                  />
                </div>
              </div>

              <div style={styles.settingRow}>
                <div>
                  <div style={styles.settingLabel}>Auto-play Responses</div>
                  <div style={styles.settingDesc}>
                    Automatically play audio responses
                  </div>
                </div>
                <div style={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={autoPlayResponses}
                    onChange={(e) => setAutoPlayResponses(e.target.checked)}
                    style={styles.checkbox}
                  />
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Appearance</h3>

              <div style={styles.settingRow}>
                <div>
                  <div style={styles.settingLabel}>Dark Mode</div>
                  <div style={styles.settingDesc}>
                    Use dark color scheme
                  </div>
                </div>
                <div style={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={isDarkMode}
                    onChange={toggleTheme}
                    style={styles.checkbox}
                  />
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Notifications</h3>

              <div style={styles.settingRow}>
                <div>
                  <div style={styles.settingLabel}>Push Notifications</div>
                  <div style={styles.settingDesc}>
                    Receive browser notifications
                  </div>
                </div>
                <div style={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={pushNotifications}
                    onChange={(e) => setPushNotifications(e.target.checked)}
                    style={styles.checkbox}
                  />
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>App Data</h3>

              <button onClick={handleResetOnboarding} style={styles.actionButton}>
                Reset Onboarding
              </button>

              <button onClick={handleClearPreferences} style={styles.actionButton}>
                Reset Preferences
              </button>
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div style={styles.section}>
            <div style={styles.card}>
              <h3 style={styles.cardTitle}>About Syntari</h3>
              <div style={styles.aboutText}>
                <p style={styles.aboutParagraph}>
                  <strong>Syntari</strong>
                </p>
                <p style={styles.aboutParagraph}>
                  Version 1.0.0
                </p>
                <p style={styles.aboutParagraph}>
                  Your AI-powered voice companion for seamless conversations and journaling.
                </p>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Color Palette</h3>
              <div style={styles.colorPalette}>
                <div style={styles.colorItem}>
                  <div style={{...styles.colorSwatch, background: '#00D46A'}}></div>
                  <div style={styles.colorLabel}>Primary</div>
                  <div style={styles.colorCode}>#00D46A</div>
                </div>
                <div style={styles.colorItem}>
                  <div style={{...styles.colorSwatch, background: '#0A0A0A'}}></div>
                  <div style={styles.colorLabel}>Black</div>
                  <div style={styles.colorCode}>#0A0A0A</div>
                </div>
                <div style={styles.colorItem}>
                  <div style={{...styles.colorSwatch, background: '#FFFFFF', border: '1px solid #2A2A2A'}}></div>
                  <div style={styles.colorLabel}>White</div>
                  <div style={styles.colorCode}>#FFFFFF</div>
                </div>
              </div>
            </div>

            <div style={styles.card}>
              <h3 style={styles.cardTitle}>Features</h3>
              <ul style={styles.featureList}>
                <li style={styles.featureItem}>Real-time voice recognition</li>
                <li style={styles.featureItem}>AI-powered responses</li>
                <li style={styles.featureItem}>Smart search with AI summaries</li>
                <li style={styles.featureItem}>Trigger word activation</li>
                <li style={styles.featureItem}>Persistent voice journal</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const getStyles = (theme) => ({
  container: {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    padding: '16px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '20px',
    gap: '12px',
  },
  backButton: {
    background: 'transparent',
    border: 'none',
    color: theme.primary,
    fontSize: '15px',
    cursor: 'pointer',
    padding: '6px',
    fontWeight: '600',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: theme.text,
    margin: 0,
  },
  tabs: {
    display: 'flex',
    gap: '0',
    borderBottom: `1px solid ${theme.border}`,
    marginBottom: '20px',
  },
  tab: {
    flex: 1,
    padding: '10px 12px',
    background: 'transparent',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color 0.2s, border-color 0.2s',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  card: {
    background: theme.surface,
    borderRadius: '14px',
    padding: '16px',
    border: `1px solid ${theme.border}`,
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: theme.text,
    marginBottom: '14px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${theme.border}`,
  },
  label: {
    color: theme.textMuted,
    fontSize: '13px',
    fontWeight: '500',
  },
  value: {
    color: theme.text,
    fontSize: '13px',
    fontWeight: '500',
  },
  valueSmall: {
    color: theme.textMuted,
    fontSize: '10px',
    fontFamily: 'monospace',
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  nameDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  nameEditContainer: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  nameInput: {
    padding: '8px 10px',
    fontSize: '13px',
    borderRadius: '8px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    outline: 'none',
    minWidth: '140px',
  },
  editButton: {
    padding: '4px 10px',
    fontSize: '11px',
    borderRadius: '6px',
    border: `1px solid ${theme.primary}`,
    background: 'transparent',
    color: theme.primary,
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.2s',
  },
  saveButton: {
    padding: '8px 14px',
    fontSize: '11px',
    borderRadius: '6px',
    border: 'none',
    background: theme.primary,
    color: theme.background,
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'opacity 0.2s',
  },
  cancelButton: {
    padding: '8px 14px',
    fontSize: '11px',
    borderRadius: '6px',
    border: `1px solid ${theme.border}`,
    background: 'transparent',
    color: theme.text,
    cursor: 'pointer',
    fontWeight: '600',
  },
  settingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 0',
    borderBottom: `1px solid ${theme.border}`,
  },
  settingLabel: {
    color: theme.text,
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '3px',
  },
  settingDesc: {
    color: theme.textMuted,
    fontSize: '11px',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    accentColor: theme.primary,
  },
  actionButton: {
    width: '100%',
    padding: '12px',
    fontSize: '13px',
    borderRadius: '10px',
    border: `1px solid ${theme.border}`,
    background: 'transparent',
    color: theme.text,
    cursor: 'pointer',
    fontWeight: '500',
    marginBottom: '10px',
    transition: 'background-color 0.2s',
  },
  signOutButton: {
    width: '100%',
    padding: '14px',
    fontSize: '15px',
    borderRadius: '12px',
    border: `2px solid ${theme.error}`,
    background: 'transparent',
    color: theme.error,
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'background-color 0.2s, color 0.2s',
  },
  aboutText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  aboutParagraph: {
    color: theme.textTertiary,
    fontSize: '13px',
    lineHeight: '1.6',
    margin: 0,
  },
  colorPalette: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
  },
  colorItem: {
    textAlign: 'center',
  },
  colorSwatch: {
    height: '50px',
    borderRadius: '10px',
    marginBottom: '6px',
  },
  colorLabel: {
    fontSize: '10px',
    color: theme.textMuted,
    marginBottom: '3px',
    fontWeight: '500',
  },
  colorCode: {
    fontSize: '9px',
    color: theme.primary,
    fontFamily: 'monospace',
  },
  featureList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  featureItem: {
    color: theme.textSecondary,
    fontSize: '13px',
    padding: '7px 0',
    paddingLeft: '18px',
    position: 'relative',
    '::before': {
      content: '"•"',
      position: 'absolute',
      left: 0,
      color: theme.primary,
    },
  },
});

export default Settings;
